(function () {
  'use strict';

  class StoneCartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-sd-cart-drawer]');
      this.overlay = document.querySelector('[data-sd-cart-overlay]');
      this.configEl = document.querySelector('[data-sd-cart-config]');

      if (!this.drawer || !this.configEl) return;

      try {
        this.config = JSON.parse(this.configEl.textContent || '{}');
      } catch (error) {
        this.config = {};
      }

      this.cart = null;
      this._lastUpsellProductId = null;
      this._upsellProducts = [];
      this._mutationController = null;
      /** @type {Set<string>} */
      this._pendingZeroKeys = new Set();
      /** @type {Map<string, ReturnType<typeof setTimeout>>} */
      this._qtyTimers = new Map();
      this._pendingCartAdd = false;
      this._spDefaultTriggered = false;
      this._removingGift = false;
      this._scrollY = 0;
      this.bindEvents();
      this.setPaymentIcons();
    }

    setPaymentIcons() {
      // Apple Pay: Safari/iOS only — detectable via browser API
      try {
        const aps = /** @type {any} */ (window).ApplePaySession;
        if (aps && aps.canMakePayments()) {
          document.body.classList.add('apple_pay');
        }
      } catch (_) {}

      // All other wallets (Google Pay, PayPal, etc.) — driven by Shopify-injected
      // wallet-configs. Google Pay is hidden on Safari/Apple devices (those users
      // have Apple Pay; Google Pay doesn't work natively in Safari).
      const isAppleDevice = !!(/** @type {any} */ (window).ApplePaySession);
      const applyWalletConfigs = () => {
        const wallet = document.querySelector('[wallet-configs]');
        if (!(wallet instanceof HTMLElement)) return false;
        let configs = [];
        try { configs = JSON.parse(wallet.getAttribute('wallet-configs') || '[]'); } catch { return false; }
        if (!Array.isArray(configs) || configs.length === 0) return false;
        for (const w of configs) {
          if (!w.name || w.name === 'apple_pay') continue;
          if (w.name === 'google_pay' && isAppleDevice) continue;
          document.body.classList.add(w.name);
        }
        return true;
      };

      if (!applyWalletConfigs()) {
        const observer = new MutationObserver(() => {
          if (applyWalletConfigs()) observer.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['wallet-configs'] });
      }
    }

    bindEvents() {
      document.addEventListener('click', (event) => {
        const target = /** @type {Element|null} */ (event.target);
        const openTrigger = target?.closest('[data-sd-cart-open]');
        if (openTrigger) {
          event.preventDefault();
          this.open();
        }

        const closeTrigger = target?.closest('[data-sd-cart-close]');
        if (closeTrigger) {
          event.preventDefault();
          this.close();
        }
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      if (!this.drawer) return;
      this.drawer.addEventListener('click', async (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const qtyButton = target.closest('[data-sd-cart-qty-change]');
        const removeButton = target.closest('[data-sd-cart-remove]');
        const upsellButton = target.closest('[data-sd-upsell-add]');

        if (qtyButton) {
          event.preventDefault();
          if (qtyButton.dataset.keys) {
            const keys = JSON.parse(qtyButton.dataset.keys);
            const delta = Number(qtyButton.dataset.delta);
            const key = /** @type {string} */ (keys[0]);

            // Update the qty span immediately on every click for instant feedback.
            const qtySpan = qtyButton.closest('.sd-cart-item__qty')
              ?.querySelector('.sd-cart-item__qty-value');
            if (qtySpan) {
              const current = Number(qtySpan.textContent) || 0;
              qtySpan.textContent = String(Math.max(0, current + delta));
            }

            // Debounce the API call — reset the 300ms window on every click so
            // rapid taps collapse into a single request.
            if (this._qtyTimers.has(key)) clearTimeout(this._qtyTimers.get(key));
            const timerId = setTimeout(async () => {
              this._qtyTimers.delete(key);
              const finalQty = qtySpan ? Number(qtySpan.textContent) : NaN;
              await this.changeGroupedLines(keys, 0, finalQty);
            }, 300);
            this._qtyTimers.set(key, timerId);
          } else {
            await this.changeLine(qtyButton.dataset.key, Number(qtyButton.dataset.quantity));
          }
        }

        if (removeButton) {
          event.preventDefault();
          if (removeButton.dataset.keys) {
            const keys = JSON.parse(removeButton.dataset.keys);
            await this.removeGroupedLines(keys);
          } else {
            await this.changeLine(removeButton.dataset.key, 0);
          }
        }

        if (upsellButton) {
          event.preventDefault();
          await this.addVariant(Number(upsellButton.dataset.variantId), 1);
        }

        const giftTarget = /** @type {Element|null} */ (event.target ?? null);
        const giftAddBtn = giftTarget?.closest('[data-sd-gift-add]');
        if (giftAddBtn) {
          event.preventDefault();
          await this.addFreeGift();
        }

        const giftRemoveBtn = giftTarget?.closest('[data-sd-gift-remove]');
        if (giftRemoveBtn) {
          event.preventDefault();
          await this.removeFreeGift();
        }
      });

      this.drawer.addEventListener('click', (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (!target) return;

        const toggle = target.closest('[data-sd-discount-toggle]');
        if (toggle) {
          const body = /** @type {HTMLElement|null} */ (this.drawer?.querySelector('[data-sd-discount-body]') ?? null);
          const icon = /** @type {HTMLElement|null} */ (toggle.querySelector('.sd-cart__discount-icon'));
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!expanded));
          if (body) body.hidden = expanded;
          if (icon) icon.textContent = expanded ? '+' : '−';
        }

        const applyBtn = target.closest('[data-sd-discount-apply]');
        if (applyBtn) {
          const input = /** @type {HTMLInputElement|null} */ (this.drawer?.querySelector('[data-sd-discount-input]') ?? null);
          if (input && input.value.trim()) {
            this.applyDiscount(input.value.trim());
          }
        }

        const removeDiscountBtn = /** @type {HTMLElement|null} */ (target.closest('[data-sd-discount-remove]'));
        if (removeDiscountBtn) {
          const code = removeDiscountBtn.getAttribute('data-sd-discount-remove');
          if (code) this.removeDiscount(code);
        }

        if (target.closest('[data-sd-expresspay-trigger]')) {
          const overlay = this.drawer?.querySelector('.additional-checkout-button__overlay');
          overlay?.classList.add('active');

          // shopify-apple-pay-button initialises in connectedCallback. At page load
          // the drawer is off-screen (translateX), so Shopify's visibility check
          // fails and the button stays hidden. Re-inserting the element now (while
          // the overlay is visible) re-fires connectedCallback so Apple Pay renders.
          // if (overlay) {
          //   overlay.querySelectorAll('shopify-apple-pay-button, shopify-dynamic-checkout-button').forEach((el) => {
          //     const clone = el.cloneNode(true);
          //     el.parentNode?.replaceChild(clone, el);
          //   });
          // }

          // Fallback: try Shopify's own PaymentButton init if available
          try {
            const spb = /** @type {any} */ (window).Shopify?.PaymentButton;
            if (spb && typeof spb.init === 'function') spb.init();
          } catch (_) {}
        }

        if (target.closest('.button-close-additional-checkout')) {
          this.drawer?.querySelector('.additional-checkout-button__overlay')?.classList.remove('active');
        }
      });

      this.drawer.addEventListener('change', async (event) => {
        const shippingToggle = event.target.closest('[data-sd-shipping-protection-toggle]');
        if (shippingToggle) {
          await this.toggleShippingProtection(shippingToggle.checked);
        }
      });

      document.addEventListener('cart:refresh', async () => {
        await this.refreshCart();
      });

      // Legacy alias kept for any existing callers.
      document.addEventListener('cart:open', () => this.open());

      // Header cart icon fires this event (snippets/cart-drawer.liquid).
      document.addEventListener('cart-drawer:open', () => this.open());

      // Product form fires cart:adding the instant Add-to-Cart is clicked,
      // before the /cart/add.js fetch starts. Show the drawer immediately so
      // it feels instant — the cart:update listener below will refresh the
      // content once the network round-trip completes.
      document.addEventListener('cart:adding', () => {
        this._pendingCartAdd = true;
      });

      // Fired after any successful cart mutation (add, change, discount).
      document.addEventListener('cart:update', async (/** @type {CustomEvent} */ event) => {
        if (event.detail?.data?.source === 'sd-cart-drawer') return;
        if (this._pendingCartAdd) {
          this._pendingCartAdd = false;
          await this.open();
        } else if (this.drawer?.classList.contains('is-active')) {
          await this.refreshCart();
        }
      });
    }

    // Show the drawer visually without fetching cart data.
    _show() {
      this.drawer.classList.add('is-active');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('sd-cart-open');

      this._scrollY = window.scrollY || 0;
      document.body.classList.add('mini-cart-open');
      Object.assign(document.body.style, {
        position: 'fixed',
        top: `-${this._scrollY}px`,
        left: '0',
        right: '0',
        width: '100%',
      });

      /** @type {any} */ (window).clarity?.('set', 'miniCart', 'open');
      hj('event', 'minicart_opened');

      if (this.overlay) {
        this.overlay.hidden = false;
        requestAnimationFrame(() => this.overlay.classList.add('is-active'));
      }
    }

    async open() {
      this._spDefaultTriggered = false;
      this._show();
      await this.refreshCart();
    }

    close() {
      this.drawer.classList.remove('is-active');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('sd-cart-open');

      document.body.classList.remove('mini-cart-open');
      Object.assign(document.body.style, {
        position: '',
        top: '',
        left: '',
        right: '',
        width: '',
      });
      window.scrollTo(0, this._scrollY || 0);

      if (this.overlay) {
        this.overlay.classList.remove('is-active');
        window.setTimeout(() => {
          if (!this.overlay.classList.contains('is-active')) {
            this.overlay.hidden = true;
          }
        }, 250);
      }
    }

    localeUrl(path) {
      const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      return `${root.replace(/\/$/, '')}${path}`;
    }

    async request(path, options = {}) {
      const response = await fetch(this.localeUrl(path), {
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        ...options
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Cart request failed');
      }

      return response.json();
    }

    async refreshCart() {
      this.setLoading(true);
      try {
        this.cart = await this.request('/cart.js', { method: 'GET' });
        this.renderCart(this.cart);
      } catch (error) {
        console.error(error);
      } finally {
        this.setLoading(false);
      }
    }

    _newMutationSignal() {
      if (this._mutationController) this._mutationController.abort();
      this._mutationController = new AbortController();
      return this._mutationController.signal;
    }

    /**
     * Merges _pendingZeroKeys into baseUpdates so in-flight removals aren't lost on abort.
     * @param {Record<string, number>} baseUpdates
     * @returns {{ updates: Record<string, number> }}
     */
    _buildGiftSyncUpdates(baseUpdates) {
      const updates = /** @type {Record<string, number>} */ ({ ...baseUpdates });
      const liveKeys = new Set((this.cart?.items || []).map((/** @type {any} */ i) => i.key));
      for (const key of (this._pendingZeroKeys || [])) {
        if (!Object.prototype.hasOwnProperty.call(updates, key) && liveKeys.has(key)) updates[key] = 0;
      }
      return { updates };
    }

    /**
     * Filters pending-deletion items out of a server cart response before storing it locally.
     * Prevents aborted removals from re-entering this.cart.items and causing stale-key 422 errors.
     * @param {any} cart
     * @returns {any}
     */
    _mergeServerCart(cart) {
      if (!this._pendingZeroKeys.size) return cart;
      const items = (cart.items || []).filter((/** @type {any} */ i) => !this._pendingZeroKeys.has(i.key));
      const item_count = items.reduce((/** @type {number} */ s, /** @type {any} */ i) => s + i.quantity, 0);
      const total_price = items.reduce((/** @type {number} */ s, /** @type {any} */ i) => s + i.final_line_price, 0);
      return { ...cart, items, item_count, total_price };
    }

    async changeLine(key, quantity) {
      if (!key) return;
      const signal = this._newMutationSignal();
      let updates = /** @type {Record<string, number>} */ ({});
      try {
        const baseUpdates = { [key]: Math.max(0, quantity) };
        ({ updates } = this._buildGiftSyncUpdates(baseUpdates));
        const cart = await this.request('/cart/update.js', { method: 'POST', body: JSON.stringify({ updates }), signal });
        this.cart = this._mergeServerCart(cart);
        this._renderCartUI(this.cart, { skipProgress: true });
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (error.message && error.message.includes('updates parameter is invalid')) {
          const safeUpdates = Object.fromEntries(Object.entries(updates).filter(([, v]) => Number(v) > 0));
          if (Object.keys(safeUpdates).length) {
            try {
              const cart = await this.request('/cart/update.js', { method: 'POST', body: JSON.stringify({ updates: safeUpdates }) });
              this.cart = this._mergeServerCart(cart);
              this._renderCartUI(this.cart, { skipProgress: true });
            } catch (retryErr) { console.error(retryErr); await this.refreshCart(); }
          } else { await this.refreshCart(); }
          return;
        }
        console.error(error);
        await this.refreshCart();
      }
    }

    /**
     * Instantly mutates local cart state and re-renders without a network wait.
     * @param {Array<{key: string, newQty: number}>} mutations
     */
    _applyCartMutation(mutations) {
      if (!this.cart) return;
      const items = this.cart.items.map((/** @type {any} */ item) => ({ ...item }));
      for (const { key, newQty } of mutations) {
        const idx = items.findIndex((/** @type {any} */ i) => i.key === key);
        if (idx === -1) continue;
        if (newQty <= 0) {
          items.splice(idx, 1);
        } else {
          items[idx] = {
            ...items[idx],
            quantity: newQty,
            final_line_price: items[idx].final_price * newQty,
            original_line_price: items[idx].original_price * newQty
          };
        }
      }

      const item_count = items.reduce((/** @type {number} */ s, /** @type {any} */ i) => s + i.quantity, 0);
      const total_price = items.reduce((/** @type {number} */ s, /** @type {any} */ i) => s + i.final_line_price, 0);
      this.cart = { ...this.cart, items, item_count, total_price };
      this._renderCartUI(this.cart);
    }

    /** @param {string[]} keys @param {number} delta @param {number} [displayedQty] */
    async changeGroupedLines(keys, delta, displayedQty) {
      if (!keys || !keys.length || !this.cart) return;

      const key = /** @type {string} */ (keys[0]);
      /** @type {any[]} */
      const cartItems = this.cart.items;
      const giftVid = Number(this.config.freeGiftVariantId || 0);
      const item = /** @type {any} */ (cartItems.find((/** @type {any} */ i) =>
        i.key === key && !(giftVid && Number(i.variant_id) === giftVid && i.final_price === 0)
      ));
      if (!item) return;

      const currentQty = (displayedQty != null && !isNaN(displayedQty)) ? displayedQty : item.quantity;
      const newQty = Math.max(0, currentQty + delta);
      const mutations = [{ key, newQty }];

      this._applyCartMutation(mutations);

      const signal = this._newMutationSignal();
      let updates = /** @type {Record<string, number>} */ ({});
      try {
        const baseUpdates = /** @type {Record<string, number>} */ ({ [key]: newQty });
        ({ updates } = this._buildGiftSyncUpdates(baseUpdates));
        const cart = await this.request('/cart/update.js', { method: 'POST', body: JSON.stringify({ updates }), signal });
        const serverKeys = new Set((cart.items || []).map((/** @type {any} */ i) => i.key));
        for (const k of Array.from(this._pendingZeroKeys)) {
          if (!serverKeys.has(k)) this._pendingZeroKeys.delete(k);
        }
        this.cart = this._mergeServerCart(cart);
        this._renderCartUI(this.cart, { skipProgress: true });
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (err.message && err.message.includes('updates parameter is invalid')) {
          const safeUpdates = /** @type {Record<string, number>} */ (Object.fromEntries(Object.entries(updates).filter(([, v]) => Number(v) > 0)));
          if (Object.keys(safeUpdates).length) {
            try {
              const cart = await this.request('/cart/update.js', { method: 'POST', body: JSON.stringify({ updates: safeUpdates }) });
              this.cart = this._mergeServerCart(cart);
              this._renderCartUI(this.cart, { skipProgress: true });
            } catch (retryErr) { console.error(retryErr); await this.refreshCart(); }
          } else { await this.refreshCart(); }
          return;
        }
        console.error(err);
        await this.refreshCart();
      }
    }

    /** @param {string[]} keys */
    async removeGroupedLines(keys) {
      if (!keys || !keys.length || !this.cart) return;

      /** @type {any[]} */
      const cartItems = this.cart.items;
      /** @type {Array<{key: string, newQty: number}>} */
      const mutations = [];

      const removeGiftVid = Number(this.config.freeGiftVariantId || 0);
      for (const k of keys) {
        const it = /** @type {any} */ (cartItems.find((/** @type {any} */ i) => i.key === k));
        if (it && !(removeGiftVid && Number(it.variant_id) === removeGiftVid && it.final_price === 0)) {
          mutations.push({ key: /** @type {string} */ (it.key), newQty: 0 });
        }
        this._pendingZeroKeys.add(k);
      }

      if (!mutations.length) return;

      this._applyCartMutation(mutations);

      const signal = this._newMutationSignal();
      try {
        const baseUpdates = /** @type {Record<string, number>} */ ({});
        for (const { key } of mutations) baseUpdates[key] = 0;
        const { updates } = this._buildGiftSyncUpdates(baseUpdates);
        const cart = await this.request('/cart/update.js', { method: 'POST', body: JSON.stringify({ updates }), signal });
        const serverKeys = new Set((cart.items || []).map((/** @type {any} */ i) => i.key));
        for (const key of Array.from(this._pendingZeroKeys)) {
          if (!serverKeys.has(key)) this._pendingZeroKeys.delete(key);
        }
        this.cart = this._mergeServerCart(cart);
        this._renderCartUI(this.cart, { skipProgress: true });
      } catch (err) {
        if (err.name === 'AbortError') return;
        // 422: one of the keys no longer exists on the server — just refresh to get clean state
        if (err.message && err.message.includes('updates parameter is invalid')) {
          this._pendingZeroKeys.clear();
          await this.refreshCart();
          return;
        }
        console.error(err);
        await this.refreshCart();
      }
    }

    async addVariant(variantId, quantity = 1) {
      if (!variantId) return;
      this.setLoading(true);
      try {
        await this.request('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ id: variantId, quantity })
        });
        // await this.refreshCart();
        this.open();
      } catch (error) {
        console.error(error);
      } finally {
        this.setLoading(false);
      }
    }

    async toggleShippingProtection(checked) {
      const variantId = Number(this.config.shippingProtectionVariantId);
      if (!variantId || !this.cart) return;

      if (checked) sessionStorage.removeItem('sd_sp_declined');
      else sessionStorage.setItem('sd_sp_declined', '1');

      const existingItems = this.cart.items.filter((/** @type {any} */ item) => Number(item.variant_id) === variantId);

      if (checked && existingItems.length === 0) {
        // Optimistic: insert a fake item immediately, background the real add
        const spPrice = Number(this.config.shippingProtectionPrice || 0);
        const tempKey = 'temp_sp_' + Date.now();
        const fakeItem = {
          key: tempKey,
          id: variantId,
          variant_id: variantId,
          product_id: 0,
          product_title: this.config.shippingProtectionLabel || 'Shipping Protection',
          title: this.config.shippingProtectionLabel || 'Shipping Protection',
          quantity: 1,
          final_price: spPrice,
          final_line_price: spPrice,
          original_price: spPrice,
          original_line_price: spPrice,
          line_price: spPrice,
          price: spPrice,
          url: '#',
          image: null,
          properties: {},
          options_with_values: [],
          product: { has_only_default_variant: true }
        };
        const items = [...this.cart.items, fakeItem];
        const item_count = items.reduce((s, /** @type {any} */ i) => s + i.quantity, 0);
        const total_price = items.reduce((s, /** @type {any} */ i) => s + i.final_line_price, 0);
        this.cart = { ...this.cart, items, item_count, total_price };
        this._renderCartUI(this.cart);

        try {
          await this.request('/cart/add.js', {
            method: 'POST',
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          const cart = await this.request('/cart.js', { method: 'GET' });
          this.cart = cart;
          this._renderCartUI(cart);
        } catch (err) {
          console.error(err);
          await this.refreshCart();
        }
        return;
      }

      if (!checked && existingItems.length > 0) {
        // Optimistic: remove items from local state instantly
        const realItems = existingItems.filter((/** @type {any} */ item) => !String(item.key).startsWith('temp_'));
        this._applyCartMutation(existingItems.map((/** @type {any} */ item) => ({ key: item.key, newQty: 0 })));
        if (realItems.length === 0) return;
        try {
          let lastCart;
          for (const item of realItems) {
            lastCart = await this.request('/cart/change.js', {
              method: 'POST',
              body: JSON.stringify({ id: item.key, quantity: 0 })
            });
          }
          if (lastCart) {
            this.cart = lastCart;
            this.renderCart(lastCart);
          }
        } catch (err) {
          console.error(err);
          await this.refreshCart();
        }
      }
    }

    /** @param {string} code */
    async applyDiscount(code) {
      const errorEl = /** @type {HTMLElement|null} */ (this.drawer?.querySelector('[data-sd-discount-error]') ?? null);
      const inputEl = /** @type {HTMLInputElement|null} */ (this.drawer?.querySelector('[data-sd-discount-input]') ?? null);
      this._clearDiscountError();

      // Block coupon if free gift is already in the cart
      const giftVidCheck = Number(this.config.freeGiftVariantId || 0);
      if (giftVidCheck && this.cart) {
        const hasGift = (this.cart.items || []).some(
          (/** @type {any} */ i) => Number(i.variant_id) === giftVidCheck && i.final_price === 0
        );
        if (hasGift) {
          if (errorEl) errorEl.textContent = 'Discount codes cannot be combined with a free gift. Please remove the free gift first.';
          return;
        }
      }

      this.setLoading(true);
      try {
        const data = await this.request('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ discount: code })
        });

        const applied = Array.isArray(data.discount_codes) &&
          data.discount_codes.some((/** @type {any} */ d) => d.code === code && d.applicable === true);

        if (!applied) {
          if (errorEl) {
            errorEl.textContent = `Discount code "${code}" is not valid or cannot be applied.`;
          }
          return;
        }

        if (inputEl) inputEl.value = '';
        const body = /** @type {HTMLElement|null} */ (this.drawer?.querySelector('[data-sd-discount-body]') ?? null);
        const toggle = /** @type {HTMLElement|null} */ (this.drawer?.querySelector('[data-sd-discount-toggle]') ?? null);
        const icon = /** @type {HTMLElement|null} */ (toggle?.querySelector('.sd-cart__discount-icon') ?? null);
        if (body) body.hidden = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        if (icon) icon.textContent = '+';

        this.cart = data;
        this.renderCart(data);
      } catch (err) {
        console.error('[StoneCartDrawer] applyDiscount failed', err);
        if (errorEl) {
          errorEl.textContent = 'Could not apply discount. Please try again.';
        }
      } finally {
        this.setLoading(false);
      }
    }

    /** @param {string} _code */
    async removeDiscount(_code) {
      this._clearDiscountError();
      this.setLoading(true);
      try {
        const cart = await this.request('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ discount: '' })
        });
        this.cart = cart;
        this.renderCart(cart);
      } catch (err) {
        console.error('[StoneCartDrawer] removeDiscount failed', err);
      } finally {
        this.setLoading(false);
      }
    }

    /** @param {any} cart */
    renderDiscounts(cart) {
      const container = /** @type {HTMLElement|null} */ (this.drawer?.querySelector('[data-sd-applied-discounts]') ?? null);
      if (!container) return;
      const codes = /** @type {any[]} */ (cart.discount_codes || []).filter((/** @type {any} */ d) => d.applicable === true);
      if (codes.length === 0) {
        container.innerHTML = '';
        return;
      }
      container.innerHTML = codes.map((/** @type {any} */ d) => `
        <div class="sd-cart__applied-discount">
          <span class="sd-cart__applied-discount-label">${this.escapeHtml(d.code)}</span>
          <button type="button" class="sd-cart__applied-discount-remove" data-sd-discount-remove="${this.escapeHtml(d.code)}" aria-label="Remove discount ${this.escapeHtml(d.code)}">✕</button>
        </div>
      `).join('');
    }

    /** @param {any} cart */
    _isThresholdMet(cart) {
      const threshold = Number(this.config.freeGiftThreshold || 0);
      const variantId = Number(this.config.freeGiftVariantId || 0);
      const shippingVariantId = Number(this.config.shippingProtectionVariantId || 0);
      if (!threshold) return false;
      let total = 0;
      for (const /** @type {any} */ item of (cart.items || [])) {
        const vid = Number(item.variant_id);
        if (shippingVariantId && vid === shippingVariantId) continue;
        if (variantId && vid === variantId && item.final_price === 0) continue;
        total += item.final_line_price;
      }
      return total >= threshold;
    }

    /** @param {any} cart @param {{ skipProgress?: boolean, skipItems?: boolean }} [opts] */
    _renderCartUI(cart, { skipProgress = false, skipItems = false } = {}) {
      const _giftVid = Number(this.config.freeGiftVariantId || 0);
      const _spVid = Number(this.config.shippingProtectionVariantId || 0);
      const displayCount = (cart.items || []).reduce((/** @type {number} */ s, /** @type {any} */ i) => {
        const vid = Number(i.variant_id);
        if (_giftVid && vid === _giftVid && i.final_price === 0) return s;
        if (_spVid && vid === _spVid) return s;
        return s + i.quantity;
      }, 0);
      const displayTotal = cart.total_price;
      this.updateText('[data-sd-cart-count]', displayCount);
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { itemCount: displayCount, source: 'sd-cart-drawer' } }
      }));
      document.querySelectorAll('.cart-bubble__text .visually-hidden').forEach((el) => {
        el.textContent = (el.textContent || '').replace(/:\s*\d+/, ': ' + displayCount);
      });
      this.updateText('[data-sd-cart-subtotal]', this.formatMoney(displayTotal));
      this.updateText('[data-sd-cart-checkout-total]', `${this.formatMoney(displayTotal)} ${this.config.currency || ''}`.trim());
      this.drawer?.classList.toggle('sd-cart--gift-unlocked', this._isThresholdMet(cart));
      if (!skipProgress) this.renderProgress(cart);
      if (!skipItems) this.renderItems(cart);
      this.renderGiftOffer(cart);
      this.renderDiscounts(cart);
      this.syncShippingProtection(cart);
      this.renderUpsells(cart).catch(console.error);

      // If threshold dropped, hide gift item immediately and remove from server
      const _giftVidCheck = Number(this.config.freeGiftVariantId || 0);
      if (_giftVidCheck && !this._isThresholdMet(cart)) {
        const realGift = (cart.items || []).find(
          (/** @type {any} */ i) => Number(i.variant_id) === _giftVidCheck && i.final_price === 0 && !String(i.key).startsWith('temp_')
        );
        if (realGift) this._silentRemoveGift(realGift.key).catch(console.error);
      }
    }

    /** @param {any} cart */
    renderCart(cart) {
      this._renderCartUI(cart);
    }
    allowBasicLineBreaks(text) {
      return String(text || '')
        .replace(/\.+$/, '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/&lt;br\s*\/?&gt;/gi, '<br>');
    }
    renderProgress(cart) {
      const threshold = Number(this.config.freeGiftThreshold || 0);
      const fill = this.drawer.querySelector('[data-sd-cart-progress-fill]');
      const text = this.drawer.querySelector('[data-sd-cart-progress-text]');

      if (!threshold || !fill || !text) return;

      const variantId = Number(this.config.freeGiftVariantId || 0);
      const shippingVariantId = Number(this.config.shippingProtectionVariantId || 0);
      let qualifyingTotal = 0;
      for (const /** @type {any} */ item of (cart.items || [])) {
        const vid = Number(item.variant_id);
        if (shippingVariantId && vid === shippingVariantId) continue;
        if (variantId && vid === variantId && item.final_price === 0) continue;
        qualifyingTotal += item.final_line_price;
      }

      const percent = Math.min(100, Math.round((qualifyingTotal / threshold) * 100));
      const remaining = Math.max(0, threshold - qualifyingTotal);
      fill.style.width = `${percent}%`;

      let targetElement = document.querySelector('.c-progress-bar .target-locks .target');
      if (remaining > 0) {
        targetElement?.classList.remove('active');
        let giftText = `Spend ${this.formatMoney(remaining)} more to receive ${this.config.freeGiftTitle} ${this.config.freeGiftMessage || ''}.`.trim();
        text.innerHTML = giftText;
      } else {
        targetElement?.classList.add('active');
        text.innerHTML = `Congrats! You have unlocked free gift.`;
      }
    }

    renderItems(cart) {
      const container = this.drawer.querySelector('[data-sd-cart-items]');
      if (!container) return;

      const spVid = Number(this.config.shippingProtectionVariantId || 0);
      const giftVid = Number(this.config.freeGiftVariantId || 0);
      const thresholdMet = this._isThresholdMet(cart);

      const isHiddenGift = (/** @type {any} */ i) =>
        giftVid && Number(i.variant_id) === giftVid && i.final_price === 0 &&
        (!thresholdMet || this._pendingZeroKeys.has(i.key));

      const hasRegular = (cart.items || []).some((/** @type {any} */ i) => {
        if (spVid && Number(i.variant_id) === spVid) return false;
        if (isHiddenGift(i)) return false;
        return true;
      });

      if (!hasRegular) {
        container.innerHTML = '<p class="sd-cart__empty">Your cart is currently empty.</p>';
        return;
      }

      const items = (cart.items || [])
        .filter((/** @type {any} */ item) => {
          if (spVid && Number(item.variant_id) === spVid) return false;
          if (isHiddenGift(item)) return false;
          return true;
        })
        .map((/** @type {any} */ item) => ({ ...item, _keys: [item.key] }));
      container.innerHTML = items.map((/** @type {any} */ item) => this.itemTemplate(item)).join('');
    }

    itemTemplate(item) {
      const shippingProtectionVariantId = Number(this.config.shippingProtectionVariantId);
      if (item.id === shippingProtectionVariantId) return '';

      const itemUrl = this.escapeHtml(item.url || '#');
      const image = item.image
        ? `<a href="${itemUrl}" class="sd-cart-item__image-link"><img class="sd-cart-item__image" src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title)}" loading="lazy" width="72" height="72"></a>`
        : `<a href="${itemUrl}" class="sd-cart-item__image-link"><div class="sd-cart-item__image sd-cart-item__image--placeholder">Product<br>Image</div></a>`;

      const options = (item.options_with_values || [])
        .filter((option) => option.value && option.value !== 'Default Title')
        .map((option) => `<span>${this.escapeHtml(option.name)}: ${this.escapeHtml(option.value)}</span>`)
        .join('');
        const freeGiftVariantId = Number(this.config.freeGiftVariantId || 0);
      const isFreeGift = freeGiftVariantId > 0 && Number(item.variant_id) === freeGiftVariantId && item.final_price === 0;
        const hasSalePrice = item.original_price > item.final_price;
        const hasLineSalePrice = item.original_line_price > item.final_line_price;

        let itemPriceHtml = '';

        if (isFreeGift && item.original_price > 0) {
          itemPriceHtml = `
            <div class="sd-cart-item__price-wrap sd-cart-item__price-wrap--free-gift">
              <span class="sd-cart-item__free-gift-badge">
                FREE GIFT JUST FOR YOU
              </span>

              <div class="sd-cart-item__price-row">
                <div class="sd-cart-item__price-row-inner">
                  <span class="sd-cart-item__price sd-cart-item__price--free-gift">
                    ${this.formatMoney(item.final_price)}
                  </span>

                  <span class="sd-cart-item__compare-price">
                    ${this.formatMoney(item.original_price)}
                  </span>
                </div>

                <div class="sd-cart-item__controls">
                  <button
                    class="sd-cart-item__delete"
                    type="button"
                    aria-label="Remove free gift"
                    data-sd-gift-remove
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14H6L5 6"/>
                      <path d="M10 11v6M14 11v6"/>
                      <path d="M9 6V4h6v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          `;
        } else if (hasSalePrice) {
          itemPriceHtml = `
            <div class="sd-cart-item__price-wrap">
              <span class="sd-cart-item__price sd-cart-item__price--sale">
                ${this.formatMoney(item.final_price)}
              </span>

              <span class="sd-cart-item__compare-price">
                ${this.formatMoney(item.original_price)}
              </span>
            </div>
          `;
        } else {
          itemPriceHtml = `
            <div class="sd-cart-item__price-wrap">
              <span class="sd-cart-item__price">
                ${this.formatMoney(item.final_price)}
              </span>
            </div>
          `;
        }

        let linePriceHtml = '';

        if (item.final_line_price === 0 && item.original_line_price > 0) {
          linePriceHtml = `
            <span class="sd-cart-item__line-price">
              ${this.formatMoney(item.final_line_price)}
            </span>
          `;
        } else if (hasLineSalePrice) {
          linePriceHtml = `
            <span class="sd-cart-item__line-price-wrap">
              <span class="sd-cart-item__line-price sd-cart-item__line-price--sale">
                ${this.formatMoney(item.final_line_price)}
              </span>

              <span class="sd-cart-item__compare-price">
                ${this.formatMoney(item.original_line_price)}
              </span>
            </span>
          `;
        } else {
          linePriceHtml = `
            <span class="sd-cart-item__line-price">
              ${this.formatMoney(item.final_line_price)}
            </span>
          `;
        }
        
        return `
          <article class="sd-cart-item" data-line-key="${this.escapeHtml(item.key)}"${isFreeGift ? ' data-free-gift="manual"' : ''}>
            ${image}

            <div class="sd-cart-item__details">
              <a class="sd-cart-item__name" href="${this.escapeHtml(item.url || '#')}">
                ${this.escapeHtml(item.product_title)}
              </a>

              ${options ? `<div class="sd-cart-item__options">${options}</div>` : ''}

              ${itemPriceHtml}

              ${!isFreeGift ? `
                <div class="sd-cart-item__controls">
                  <div class="sd-cart-item__qty">
                    <button
                      class="sd-cart-item__qty-btn"
                      type="button"
                      aria-label="Decrease quantity"
                      data-sd-cart-qty-change
                      data-keys="${this.escapeHtml(JSON.stringify(item._keys))}"
                      data-delta="-1"
                    >
                      −
                    </button>

                    <span class="sd-cart-item__qty-value">${item.quantity}</span>

                    <button
                      class="sd-cart-item__qty-btn"
                      type="button"
                      aria-label="Increase quantity"
                      data-sd-cart-qty-change
                      data-keys="${this.escapeHtml(JSON.stringify(item._keys))}"
                      data-delta="1"
                    >
                      +
                    </button>
                  </div>

                  <button
                    class="sd-cart-item__delete"
                    type="button"
                    aria-label="Remove item"
                    data-sd-cart-remove
                    data-keys="${this.escapeHtml(JSON.stringify(item._keys))}"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14H6L5 6"/>
                      <path d="M10 11v6M14 11v6"/>
                      <path d="M9 6V4h6v2"/>
                    </svg>
                  </button>
                </div>
              ` : ''}
            </div>

            ${linePriceHtml}
          </article>
        `;
    }

    async renderUpsells(cart) {
      const wrap = this.drawer.querySelector('[data-sd-cart-upsells]');
      const list = this.drawer.querySelector('[data-sd-cart-upsell-list]');
      if (!wrap || !list) return;

      const baseUrl = this.config.recommendationsUrl;
      if (!baseUrl || !cart || !cart.items.length) {
        wrap.style.display = 'none';
        return;
      }

      const shippingVariantId = Number(this.config.shippingProtectionVariantId || 0);
      const giftVariantId = Number(this.config.freeGiftVariantId || 0);
      const baseItem = cart.items.find((item) => {
        const vid = Number(item.variant_id);
        return vid !== shippingVariantId && vid !== giftVariantId;
      });

      if (!baseItem) {
        wrap.style.display = 'none';
        return;
      }

      // Only re-fetch when the base product changes; otherwise reuse cached products.
      if (this._lastUpsellProductId !== baseItem.product_id) {
        this._lastUpsellProductId = baseItem.product_id;
        this._upsellProducts = [];
        const limit = Number(this.config.upsellLimit || 4);
        const intent = this.config.upsellIntent || 'related';
        const url = `${baseUrl}?product_id=${baseItem.product_id}&limit=${limit}&intent=${intent}`;
        try {
          const data = await fetch(url, { credentials: 'same-origin' }).then((r) => r.json());
          this._upsellProducts = data.products || [];
        } catch (err) {
          console.error('[StoneCartDrawer] recommendations fetch failed', err);
        }
      }

      // Always re-filter against current cart so added upsells disappear immediately.
      const cartVariantIds = cart.items.map((/** @type {any} */ item) => Number(item.variant_id));
      /** @type {any[]} */
      const visible = (/** @type {any[]} */ (this._upsellProducts)).filter((/** @type {any} */ p) => {
        const variant = p.variants && p.variants[0];
        return variant && variant.available && !cartVariantIds.includes(Number(variant.id));
      });

      if (!visible.length) {
        list.innerHTML = '';
        wrap.style.display = 'none';
        return;
      }

      const titleEl = wrap.querySelector('[data-sd-cart-upsell-title]');
      if (titleEl && this.config.upsellTitle) titleEl.textContent = this.config.upsellTitle;
      wrap.style.display = '';

      list.innerHTML = visible.map((product) => {
        const variant = product.variants[0];
        const image = product.featured_image
          ? `<img class="sd-upsell-item__image" src="${this.escapeHtml(product.featured_image)}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="64" height="64">`
          : '<div class="sd-upsell-item__image sd-upsell-item__image--placeholder">Product<br>Image</div>';

        return `
          <article class="sd-upsell-item">
            ${image}
            <div class="sd-upsell-item__info">
              <a class="sd-upsell-item__name" href="${this.escapeHtml(product.url || '#')}">${this.escapeHtml(product.title)}</a>
              <p class="sd-upsell-item__price">${this.formatMoney(variant.price)}</p>
              <button class="sd-upsell-item__btn" type="button" data-sd-upsell-add data-variant-id="${variant.id}">
                <span aria-hidden="true">+</span>
                Add to Order
              </button>
            </div>
          </article>
        `;
      }).join('');
    }

    _clearDiscountError() {
      const errorEl = /** @type {HTMLElement|null} */ (this.drawer?.querySelector('[data-sd-discount-error]') ?? null);
      if (errorEl) errorEl.textContent = '';
    }

    /** @param {any} cart */
    renderGiftOffer(cart) {
      const container = /** @type {HTMLElement|null} */ (this.drawer?.querySelector('[data-sd-gift-offer]') ?? null);
      if (!container) return;
      const giftVid = Number(this.config.freeGiftVariantId || 0);
      if (!giftVid || !this._isThresholdMet(cart)) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }

      const giftInCart = (cart.items || []).some(
        (/** @type {any} */ i) => Number(i.variant_id) === giftVid && i.final_price === 0
      );
      if (giftInCart) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }

      const title = this.escapeHtml(this.config.freeGiftProductTitle || this.config.freeGiftTitle || 'Free Gift');
      const imgSrc = this.config.freeGiftImage ? this.escapeHtml(this.config.freeGiftImage) : '';
      const comparePrice = Number(this.config.freeGiftComparePrice || 0);
      const imageHtml = imgSrc
        ? `<img class="sd-cart__gift-offer-img" src="${imgSrc}" alt="${title}" width="60" height="60" loading="lazy">`
        : '';
      const priceHtml = comparePrice > 0
        ? `<s class="sd-cart__gift-offer-compare">${this.formatMoney(comparePrice)}</s>`
        : '';

      container.hidden = false;
      container.innerHTML = `
        <div class="sd-cart__gift-offer">
          ${imageHtml}
          <div class="sd-cart__gift-offer-info">
            <span class="sd-cart__gift-offer-title">${title}</span>
            <div class="sd-cart__gift-offer-pricing">
              ${priceHtml}
              <span class="sd-cart__gift-offer-free-label">FREE GIFT</span>
            </div>
          </div>
          <button type="button" class="sd-cart__gift-offer-btn sd-cart__gift-offer-btn--add" data-sd-gift-add>ADD FREE GIFT</button>
        </div>`;
    }

    async addFreeGift() {
      const giftVid = Number(this.config.freeGiftVariantId || 0);
      if (!giftVid || !this.cart) return;
      this._clearDiscountError();

      // Block free gift if a discount coupon is already applied
      const hasDiscount = Array.isArray(this.cart.discount_codes) &&
        this.cart.discount_codes.some((/** @type {any} */ d) => d.applicable === true);
      if (hasDiscount) {
        const errorEl = /** @type {HTMLElement|null} */ (this.drawer?.querySelector('[data-sd-discount-error]') ?? null);
        if (errorEl) errorEl.textContent = 'Free gift cannot be combined with a discount code. Please remove the discount first.';
        return;
      }

      // Optimistic: insert a fake gift item immediately so the UI updates instantly.
      const comparePrice = Number(this.config.freeGiftComparePrice || 0);
      const tempKey = 'temp_gift_' + Date.now();
      const giftOptions = /** @type {any[]} */ (this.config.freeGiftOptionsWithValues || []);
      const fakeGiftItem = {
        key: tempKey,
        id: giftVid,
        variant_id: giftVid,
        product_id: 0,
        product_title: this.config.freeGiftTitle || 'Free Gift',
        title: this.config.freeGiftTitle || 'Free Gift',
        quantity: 1,
        final_price: 0,
        final_line_price: 0,
        original_price: comparePrice,
        original_line_price: comparePrice,
        line_price: 0,
        price: 0,
        url: this.config.freeGiftUrl || '#',
        image: this.config.freeGiftImage || null,
        properties: {},
        options_with_values: giftOptions,
        product: { has_only_default_variant: giftOptions.length === 0 }
      };
      const items = [...this.cart.items, fakeGiftItem];
      const item_count = items.reduce((/** @type {number} */ s, /** @type {any} */ i) => s + i.quantity, 0);
      const total_price = items.reduce((/** @type {number} */ s, /** @type {any} */ i) => s + i.final_line_price, 0);
      this.cart = { ...this.cart, items, item_count, total_price };
      this._renderCartUI(this.cart);
      this.setLoading(true);

      try {
        await this.request('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ id: giftVid, quantity: 1 })
        });
        const cart = await this.request('/cart.js', { method: 'GET' });

        // Patch the fake element's key in-place to avoid re-rendering the item and causing an image flash
        const realGiftItem = /** @type {any} */ ((cart.items || []).find(
          (/** @type {any} */ i) => Number(i.variant_id) === giftVid && i.final_price === 0
        ));
        const fakeEl = this.drawer?.querySelector(`[data-line-key="${tempKey}"]`);
        if (realGiftItem && fakeEl) {
          fakeEl.setAttribute('data-line-key', realGiftItem.key);
        }

        this.cart = cart;
        this._renderCartUI(cart);
      } catch (err) {
        console.error(err);
        this.cart = null;
        await this.refreshCart();
      } finally {
        this.setLoading(false);
      }
    }

    async removeFreeGift() {
      const giftVid = Number(this.config.freeGiftVariantId || 0);
      if (!giftVid || !this.cart) return;
      this._clearDiscountError();
      const giftItem = /** @type {any} */ ((this.cart.items || []).find(
        (/** @type {any} */ i) => Number(i.variant_id) === giftVid && i.final_price === 0
      ));
      if (!giftItem) return;

      // Track as pending so concurrent changeGroupedLines responses don't re-show the gift
      this._pendingZeroKeys.add(giftItem.key);

      // Optimistic: remove gift item from local cart state immediately
      const items = (this.cart.items || []).filter(
        (/** @type {any} */ i) => !(Number(i.variant_id) === giftVid && i.final_price === 0)
      );
      const item_count = items.reduce((/** @type {number} */ s, /** @type {any} */ i) => s + i.quantity, 0);
      const total_price = items.reduce((/** @type {number} */ s, /** @type {any} */ i) => s + i.final_line_price, 0);
      this.cart = { ...this.cart, items, item_count, total_price };
      this._renderCartUI(this.cart);
      this.setLoading(true);

      try {
        const cart = await this.request('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: giftItem.key, quantity: 0 })
        });
        this._pendingZeroKeys.delete(giftItem.key);
        this.cart = cart;
        this._renderCartUI(cart);
      } catch (err) {
        this._pendingZeroKeys.delete(giftItem.key);
        console.error(err);
        this.cart = null;
        await this.refreshCart();
      } finally {
        this.setLoading(false);
      }
    }

    async _silentRemoveGift(/** @type {string} */ key) {
      if (this._removingGift) return;
      this._removingGift = true;
      try {
        const cart = await this.request('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        this.cart = cart;
        this._renderCartUI(cart);
      } catch (err) {
        console.error(err);
      } finally {
        this._removingGift = false;
      }
    }

    syncShippingProtection(cart) {
      const input = /** @type {HTMLInputElement|null} */ (this.drawer?.querySelector('[data-sd-shipping-protection-toggle]') ?? null);
      const variantId = Number(this.config.shippingProtectionVariantId);
      if (!input || !variantId) return;

      const isInCart = cart.items.some((item) => Number(item.variant_id) === variantId);

      if (isInCart) {
        input.checked = true;
        return;
      }

      if (!this._spDefaultTriggered && this.config.shippingProtectionDefaultOn && !sessionStorage.getItem('sd_sp_declined')) {
        this._spDefaultTriggered = true;
        input.checked = true;
        this.toggleShippingProtection(true);
        return;
      }

      input.checked = false;
    }

    setLoading(isLoading) {
      this.drawer.classList.toggle('is-loading', isLoading);
    }

    updateText(selector, value) {
      this.drawer.querySelectorAll(selector).forEach((el) => {
        el.textContent = value;
      });
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const currency = this.config.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol'
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    window.StoneCartDrawer = new StoneCartDrawer();
  });
})();